import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Paperclip } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { DotLoader } from '@/components/ui/DotLoader'
import { labelClass, labelStyle, textareaClass, inputStyle } from '@/lib/styleKit'
import { cn } from '@/lib/utils'
import { adminBugsApi, adminMetaApi } from '../adminApi'
import { bugStatusLabel, formatDate } from '../lib/labels'
import {
  ActivityLog,
  BUG_STATUS_TONE,
  Badge,
  ErrorBanner,
  Field,
  Panel,
  PanelHeader,
  SEVERITY_TONE,
  bodyStyle,
  metaStyle,
  titleStyle,
} from '../components/ui'
import type { BugDetail, BugPatch, BugStatus } from '../types'

const UNASSIGNED = '__none__'
const ADD_MEMBER = '__add__'

function patchError(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (detail && typeof detail === 'object') {
    return Object.entries(detail as Record<string, string>)
      .map(([k, v]) => `${k}: ${v}`)
      .join('; ')
  }
  if (typeof detail === 'string') return detail
  return 'Could not save the change.'
}

function Control({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <span className={labelClass} style={labelStyle}>
        {label}
      </span>
      {children}
      {hint && (
        <p className="mt-1 text-muted-foreground dark:text-[#8a8f98]" style={metaStyle}>
          {hint}
        </p>
      )}
    </div>
  )
}

export default function BugDetailPage() {
  const { id } = useParams()
  const bugId = Number(id)
  const qc = useQueryClient()

  const meta = useQuery({ queryKey: ['admin', 'meta'], queryFn: () => adminMetaApi.get().then((r) => r.data) })
  const bug = useQuery({
    queryKey: ['admin', 'bug', bugId],
    queryFn: () => adminBugsApi.get(bugId).then((r) => r.data),
    enabled: Number.isFinite(bugId),
  })

  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    setNotes(bug.data?.internal_notes ?? '')
  }, [bug.data?.internal_notes])

  const save = useMutation({
    mutationFn: (patch: BugPatch) => adminBugsApi.patch(bugId, patch).then((r) => r.data),
    onSuccess: (data: BugDetail) => {
      setError(null)
      qc.setQueryData(['admin', 'bug', bugId], data)
      void qc.invalidateQueries({ queryKey: ['admin', 'bugs'] })
    },
    onError: (err) => setError(patchError(err)),
  })

  const addMember = useMutation({
    mutationFn: (name: string) => adminMetaApi.addAssignee(name).then((r) => r.data),
    onSuccess: async (member) => {
      await qc.invalidateQueries({ queryKey: ['admin', 'meta'] })
      save.mutate({ assignee: member.name })
    },
    onError: (err) => setError(patchError(err)),
  })

  const onAssigneeChange = (value: string) => {
    if (value === ADD_MEMBER) {
      const name = window.prompt('Team member name')?.trim()
      if (name) addMember.mutate(name)
      return
    }
    save.mutate({ assignee: value === UNASSIGNED ? null : value })
  }

  if (!Number.isFinite(bugId)) return <ErrorBanner>Invalid bug id.</ErrorBanner>
  if (bug.isLoading) {
    return (
      <div className="py-16 flex justify-center">
        <DotLoader />
      </div>
    )
  }
  if (bug.isError || !bug.data) {
    return (
      <div className="space-y-3">
        <ErrorBanner>Bug not found.</ErrorBanner>
        <Link to="/admin/bugs" className="text-[#5e6ad2] hover:underline" style={bodyStyle}>
          Back to bugs
        </Link>
      </div>
    )
  }

  const b = bug.data
  const busy = save.isLoading || addMember.isLoading
  const assigneeNames = new Set((meta.data?.assignees ?? []).map((a) => a.name))
  const assigneeOptions = [
    { value: UNASSIGNED, label: 'Unassigned' },
    ...(meta.data?.assignees ?? []).map((a) => ({ value: a.name, label: a.name })),
    ...(b.assignee && !assigneeNames.has(b.assignee) ? [{ value: b.assignee, label: b.assignee }] : []),
    { value: ADD_MEMBER, label: '+ Add team member…' },
  ]
  const statusOptions = (meta.data?.bug_statuses ?? ['todo', 'in_progress', 'resolved']).map((s) => ({ value: s, label: bugStatusLabel(s) }))
  const moduleOptions = (meta.data?.modules ?? []).map((m) => ({ value: m, label: m }))
  const severityOptions = (meta.data?.severities ?? []).map((s) => ({ value: s, label: s }))

  const openAttachment = async () => {
    if (!b.attachment_url) return
    if (/^https?:/i.test(b.attachment_url)) {
      window.open(b.attachment_url, '_blank', 'noopener')
      return
    }
    try {
      const { data } = await adminBugsApi.attachmentBlob(b.id)
      const url = URL.createObjectURL(data)
      window.open(url, '_blank', 'noopener')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch {
      setError('Could not load the attachment.')
    }
  }

  const ctx = (b.context ?? {}) as Record<string, unknown>
  const ctxValue = (k: string) => (ctx[k] == null ? '—' : String(ctx[k]))
  const notesDirty = (notes.trim() || '') !== (b.internal_notes ?? '')

  return (
    <div className="space-y-5">
      {/* ------------------------------------------------------------ header */}
      <div className="flex items-start gap-3">
        <Link
          to="/admin/bugs"
          className="mt-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 dark:hover:bg-white/[0.04]"
          aria-label="Back to bugs"
        >
          <ArrowLeft size={16} />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap text-muted-foreground dark:text-[#8a8f98]" style={metaStyle}>
            <span className="font-mono">{b.reference_code ?? `#${b.id}`}</span>
            <Badge tone={BUG_STATUS_TONE[b.admin_status as BugStatus] ?? 'neutral'}>{bugStatusLabel(b.admin_status)}</Badge>
            {b.severity && <Badge tone={SEVERITY_TONE[b.severity] ?? 'neutral'}>{b.severity}</Badge>}
            {b.module && <Badge>{b.module}</Badge>}
            <span>· reported {formatDate(b.created_at)}</span>
          </div>
          <h1 className="mt-1 text-foreground dark:text-[#f7f8f8]" style={titleStyle}>
            {b.title}
          </h1>
        </div>
      </div>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* ------------------------------------------------------- submitted */}
        <div className="lg:col-span-3 space-y-4">
          <Panel>
            <PanelHeader title="Report" aside="As submitted by the user" />
            <div className="p-4 space-y-4">
              <Field label="Description">
                <p className="whitespace-pre-wrap">{b.description || '—'}</p>
              </Field>
              <Field label="Steps to reproduce">
                <p className="whitespace-pre-wrap">{b.steps_to_reproduce || '—'}</p>
              </Field>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Module">{b.submitted_module ?? '—'}</Field>
                <Field label="Severity">{b.submitted_severity ?? '—'}</Field>
                <Field label="Attachment">
                  {b.attachment_url ? (
                    <button
                      type="button"
                      onClick={openAttachment}
                      className="inline-flex items-center gap-1 text-[#5e6ad2] dark:text-[#a5b4fc] hover:underline"
                    >
                      <Paperclip size={13} />
                      {b.attachment_path?.split('/').pop() ?? 'Open'}
                    </button>
                  ) : (
                    '—'
                  )}
                </Field>
              </div>
            </div>
          </Panel>

          <Panel>
            <PanelHeader title="Reporter & context" aside="Captured automatically" />
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Reporter">
                {b.reporter.email ?? `user ${b.reporter.user_id}`}
                {b.reporter.full_name && <span className="text-muted-foreground dark:text-[#8a8f98]"> · {b.reporter.full_name}</span>}
              </Field>
              <Field label="Organisation">{b.reporter.account_name ?? '—'}</Field>
              <Field label="Page">{ctxValue('url')}</Field>
              <Field label="App version">{ctxValue('app_version')}</Field>
              <Field label="Browser" className="sm:col-span-2">
                <span className="text-muted-foreground dark:text-[#8a8f98] break-all" style={metaStyle}>
                  {ctxValue('user_agent')}
                </span>
              </Field>
            </div>
          </Panel>

          <Panel>
            <PanelHeader title="Activity" />
            <div className="p-4">
              <ActivityLog entries={b.activity} />
            </div>
          </Panel>
        </div>

        {/* ----------------------------------------------------------- triage */}
        <div className="lg:col-span-2 space-y-4">
          <Panel>
            <PanelHeader title="Triage" aside={busy ? 'Saving…' : undefined} />
            <div className="p-4 space-y-4">
              <Control label="Status">
                <Select value={b.admin_status} onChange={(v) => save.mutate({ admin_status: v as BugStatus })} options={statusOptions} />
              </Control>
              <Control label="Assignee" hint="Assigning a To Do bug moves it to In Progress.">
                <Select value={b.assignee ?? UNASSIGNED} onChange={onAssigneeChange} options={assigneeOptions} />
              </Control>
              <div className="grid grid-cols-2 gap-3">
                <Control label="Module">
                  <Select value={b.module ?? ''} onChange={(v) => save.mutate({ module_override: v })} options={moduleOptions} placeholder="Module" />
                </Control>
                <Control label="Severity">
                  <Select value={b.severity ?? ''} onChange={(v) => save.mutate({ severity_override: v })} options={severityOptions} placeholder="Severity" />
                </Control>
              </div>
              {(b.module_override || b.severity_override) && (
                <p className="text-muted-foreground dark:text-[#8a8f98]" style={metaStyle}>
                  Corrected by the team. The user submitted {b.submitted_module} / {b.submitted_severity}.
                </p>
              )}
              <div className="rounded-md bg-muted/50 dark:bg-white/[0.03] px-3 py-2" style={metaStyle}>
                <span className="text-muted-foreground dark:text-[#8a8f98]">User sees </span>
                <span className="text-foreground dark:text-[#f7f8f8]">{b.user_facing_status}</span>
                {b.resolved_at && <span className="text-muted-foreground dark:text-[#8a8f98]"> · resolved {formatDate(b.resolved_at)}</span>}
              </div>
            </div>
          </Panel>

          <Panel>
            <PanelHeader title="Internal notes" aside="Team only" />
            <div className="p-4 space-y-2">
              <textarea
                className={cn(textareaClass, 'min-h-[140px]')}
                style={inputStyle}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Never shown to the reporter."
              />
              <div className="flex justify-end gap-2">
                {notesDirty && (
                  <Button size="sm" variant="ghost" onClick={() => setNotes(b.internal_notes ?? '')}>
                    Discard
                  </Button>
                )}
                <Button size="sm" disabled={!notesDirty} loading={save.isLoading} onClick={() => save.mutate({ internal_notes: notes.trim() || null })}>
                  Save notes
                </Button>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  )
}
